import { Injectable } from "@angular/core";
import { MessageService } from "primeng/api";
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { AppConstants } from "../constants/app.constants";
import { HaaiError } from "../models/HaaiError";
 
@Injectable({
  providedIn: 'root'
})
export class ToastService {
  TOAST_KEY: string = 'global-toast';
  CUSTOM_TOAST_KEY: string = 'custom-toast';
  STICKY: boolean = true;
 
  // Behaviour subject to communicate toast with general app
  private overlaySubject = new BehaviorSubject<boolean>(false);
  public overlay$ = this.overlaySubject.asObservable();
 
  // Counters to show correctly in system
  private activeToastCount: number = AppConstants.ZERO_NUMBER;
  private toastIdCounter: number = AppConstants.ZERO_NUMBER;
 
  // Handle if toast is active
  private hasActiveToast: boolean = false;
 
  constructor(private msgService: MessageService,
    private translate: TranslateService) {
  }
 
  /**
   * @description Displays a success toast with the provided message details.
   * @param {string} detail The detailed message to be shown in the toast.
   * @return {Promise<void>} A promise that resolves when the toast is shown.
   */
  public async showSuccessToast(detail: string): Promise<void> {
    const summary = await lastValueFrom(this.translate.get('TOAST.SUCCESS'));
    const detailTranslated = await lastValueFrom(this.translate.get(detail));
    this.showToast(summary, detailTranslated, 'success');
  }
 
  /**
   * @description Displays an informational toast with the provided message details.
   * @param {string} detail The detailed message to be shown in the toast.
   * @return {Promise<void>} A promise that resolves when the toast is shown.
   */
  public async showInfoToast(detail: string): Promise<void> {
    const summary = await lastValueFrom(this.translate.get('TOAST.INFO'));
    const detailTranslated = await lastValueFrom(this.translate.get(detail));
    this.showToast(summary, detailTranslated, 'info');
  }
 
  /**
   * @description Displays a warning toast with the provided message details.
   * @param {string} detail The detailed message to be shown in the toast.
   * @return {Promise<void>} A promise that resolves when the toast is shown.
   */
  public async showWarnToast(detail: string): Promise<void> {
    const summary = await lastValueFrom(this.translate.get('TOAST.WARN'));
    const detailTranslated = await lastValueFrom(this.translate.get(detail));
    this.showToast(summary, detailTranslated, 'warn');
  }
 
  /**
   * @description Displays an error toast with the provided message details.
   * @param {string} detail The detailed message to be shown in the toast.
   * @return {Promise<void>} A promise that resolves when the toast is shown.
   */
  public async showErrorToast(detail: string): Promise<void> {
    const summary = await lastValueFrom(this.translate.get('TOAST.ERROR'));
    const detailTranslated = await lastValueFrom(this.translate.get(detail));
    this.showToast(summary, detailTranslated, 'error');
  }
 
  /**
   * @description Displays a custom toast with the provided message details.
   * @param {HaaiError} error The detailed message to be shown in the custom toast.
   * @return {Promise<void>} A promise that resolves when the custom toast is shown.
   */
  public async showCustomToastError(error: HaaiError): Promise<void> {
    if (this.hasActiveToast) return;
    this.showCustomizedToastError(error);
  }
 
  /**
   * @description Displays a custom toast with the provided message details.
   * @param {string} detail The detailed message to be shown in the custom toast.
   * @param {string} [traceId] Optional traceId to display in the toast.
   * @return {Promise<void>} A promise that resolves when the custom toast is shown.
   */
  public async showCustomToast(detail: string): Promise<void> {
    if (this.hasActiveToast) return;
    this.showCustomizedToast(detail);
  }
 
  /**
   * @description Closes the global toast.
   * @return {Promise<void>} A promise that resolves when the global toast is closed.
   */
  public async closeToast(): Promise<void> {
    this.msgService.clear(this.TOAST_KEY);
    this.msgService.clear(this.CUSTOM_TOAST_KEY);
    this.setOverlayVisibility(false);
    this.hasActiveToast = false;
    this.decrementToastCount();
  }
 
  /**
   * @description set overlay visibility information
   * @param {boolean} visible informatio to show or hide visibility
   */
  private setOverlayVisibility(visible: boolean) {
    this.overlaySubject.next(visible);
  }
 
  /**
   * @description Displays a custom toast with specific customization options.
   * @param {string} detail The detailed message to be shown in the custom toast.
   * @param {string} [traceId] Optional traceId to display in the toast.
   * @return {Promise<void>} A promise that resolves when the custom toast is shown.
   */
  private async showCustomizedToast(detail: string, traceId?: string): Promise<void> {
    const id = this.generateToastId();
    this.setOverlayVisibility(true);
    this.hasActiveToast = true;
 
    this.msgService.add({
      key: this.CUSTOM_TOAST_KEY,
      severity: 'custom',
      detail: detail,
      sticky: this.STICKY,
      data: {
        unique: id,
        traceId: traceId ?? null
      }
    });
 
    this.incrementToastCount();
  }
 
  /**
   * @description Displays a custom toast with specific customization options.
   * @param {string} detail The detailed message to be shown in the custom toast.
   * @param {string} [traceId] Optional traceId to display in the toast.
   * @return {Promise<void>} A promise that resolves when the custom toast is shown.
   */
  private async showCustomizedToastError(error: HaaiError): Promise<void> {
    const id = this.generateToastId();
    this.setOverlayVisibility(true);
    this.hasActiveToast = true;
 
    this.msgService.add({
      key: this.CUSTOM_TOAST_KEY,
      severity: 'custom',
      detail: error['result'] ?? error.toString(),
      sticky: this.STICKY,
      data: {
        unique: id,
        traceId: error['traceId'] ?? null,
        code: error['code'] ?? null,
        path: error['path'] ?? null
      }
    });
 
    this.incrementToastCount();
  }
 
  /**
   * @description Displays a general toast with the provided summary, detail, and severity.
   * @param {string} summary The title or summary of the toast.
   * @param {string} detail The detailed message to be shown in the toast.
   * @param {string} severity The severity of the toast (e.g., 'success', 'info', 'warn', 'error').
   * @return {Promise<void>} A promise that resolves when the toast is shown.
   */
  private async showToast(summary: string, detail: string, severity: string): Promise<void> {
    const id = this.generateToastId();
    this.msgService.add({
      key: this.TOAST_KEY,
      severity: severity,
      summary: summary,
      detail: detail,
      sticky: this.STICKY,
      data: { unique: id }
    });
    this.setOverlayVisibility(true);
    this.incrementToastCount();
  }
 
  /**
   * @description Increment the toast count and manage overlay visibility
   * @return {void}
   */
  private incrementToastCount(): void {
    this.activeToastCount++;
  }
 
  /**
   * @description Decrement the toast count and hide overlay if no more toasts are active
   * @return {void}
   */
  private decrementToastCount(): void {
    this.activeToastCount--;
    if (this.activeToastCount === AppConstants.ZERO_NUMBER) {
      this.setOverlayVisibility(false);
    }
  }
 
  /**
   * @description Generate toast id unique to show correctly
   * @returns {string} unique id information to each toast
   */
  private generateToastId(): string {
    this.toastIdCounter++;
    return `toast-${this.toastIdCounter}`;
  }
}
 